/* eslint-disable jsx-a11y/anchor-is-valid */
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Checkbox} from 'primereact/checkbox'
import {Ripple} from 'primereact/ripple'
import {Tooltip} from 'primereact/tooltip'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {HTTP_STATUS_OK} from '../../Constants'
import {
    createOrUpdateGenericInfo,
    getListGenericInfo,
} from '../../api/ServiceWorker'
import NoRolesImage from '../../assets/images/No-Roles.png'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {convertKeyToLabel, tableEvent} from '../table-utils/TableUtils'
import {permissionOptionKeys, roleList} from './RoleConstant'
import RoleManagementForm from './RoleManagementForm'

const ROLE_PATH = 'role'
const QueryKey = 'Role'
const fontStyle =
    'col-12 p-0 text-sm sm:text-sm md:text-xl lg:text-xl align-content-center'
const RoleManagement = () => {
    const {translateCommons} = TranslateUtils()
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return idString ? parseInt(idString) : null
    }, [idString])
    const queryClient = useQueryClient()
    const {user} = useAuth()
    const {data: roleOptions} = useQuery(
        [QueryKey, user?.id],
        () =>
            getListGenericInfo(
                ROLE_PATH,
                user?.id,
                {
                    clientId: id,
                },
                null,
                false,
            ),
        {
            enabled: !!user?.id,
        },
    )

    const formRef = useRef(null)

    const [disabled, setDisabled] = useState(true)
    // eslint-disable-next-line no-unused-vars
    const [reload, setReload] = useState(false)
    const [checked, setChecked] = useState([])
    const [active, setActive] = useState([])

    useEffect(() => {
        if (roleOptions && roleOptions?.length > 0) {
            setActive([...roleOptions?.map(role => role?.id)])
        }
    }, [roleOptions])

    const composedRoleOptions = useMemo(() => {
        if (!roleOptions) {
            return []
        }

        if (checked?.length > 0) {
            let placeholder = [...roleOptions]

            let tmp = placeholder.map(x => {
                let existData = false

                x.permissions = x.permissions.map(y => {
                    checked.forEach(z => {
                        if (
                            z.id === x.id &&
                            y.roleCategory === z.parentKey &&
                            z.key
                        ) {
                            y[z.key] = !!z.checked

                            existData = true
                        } else {
                            existData = false
                        }
                    })
                    return y
                })

                if (!existData) {
                    checked.forEach(z => {
                        if (z.id === x.id) {
                            let existRoleCategory = x.permissions.find(
                                obj => obj.roleCategory === z.parentKey,
                            )

                            if (!existRoleCategory) {
                                x.permissions.push({
                                    allowToEdit:
                                        z.key === 'allowToEdit'
                                            ? z.checked
                                            : false,
                                    allowToAdd:
                                        z.key === 'allowToAdd'
                                            ? z.checked
                                            : false,
                                    allowToDelete:
                                        z.key === 'allowToDelete'
                                            ? z.checked
                                            : false,
                                    allowToTransfer:
                                        z.key === 'allowToTransfer'
                                            ? z.checked
                                            : false,
                                    allowToGroup:
                                        z.key === 'allowToGroup'
                                            ? z.checked
                                            : false,
                                    roleCategory: z.parentKey,
                                })
                            } else {
                                existRoleCategory[z.key] = z.checked

                                // eslint-disable-next-line array-callback-return
                                x.permissions.map(obj => {
                                    if (
                                        obj.id === z.id &&
                                        obj.roleCategory === z.parentKey
                                    ) {
                                        obj[z.key] = z.checked
                                    }
                                })
                            }
                        }
                    })
                }

                return x
            })

            if (active?.length > 0) {
                // eslint-disable-next-line no-const-assign
                tmp = tmp.map(x =>
                    active.includes(x.id)
                        ? {...x, active: true}
                        : {...x, active: false},
                )
            }

            return tmp
        }

        let tmpOptions = [...roleOptions]

        if (active?.length > 0) {
            tmpOptions = tmpOptions.map(x =>
                active.includes(x.id)
                    ? {...x, active: true}
                    : {...x, active: false},
            )
        }

        return tmpOptions
    }, [roleOptions, checked, active])

    const handleOnChange = useCallback(
        (e, item) => {
            let tmp = [...checked]
            let index = tmp.findIndex(
                obj =>
                    obj.id === item.id &&
                    obj.parentKey === item.parentKey &&
                    obj.key === item.key,
            )

            if (index < 0) {
                tmp.push({
                    ...item,
                    checked: !!e.checked,
                })
            } else {
                // eslint-disable-next-line array-callback-return
                tmp.map(x => {
                    if (
                        x.id === item.id &&
                        x.parentKey === item.parentKey &&
                        x.key === item.key
                    ) {
                        x.checked = e.checked
                    }
                })
            }

            setChecked(tmp)
        },
        [checked],
    )

    const handleOnActiveChange = useCallback(
        id => {
            let tmp = [...active]
            let index = tmp.findIndex(obj => obj === id)

            if (index < 0) {
                tmp.push(id)
            } else {
                tmp.splice(index, 1)
            }

            setActive(tmp)
        },
        [active],
    )

    const dataList = useMemo(() => {
        let tmp = [...roleList]
        if (
            !composedRoleOptions ||
            !Array.isArray(composedRoleOptions) ||
            composedRoleOptions.length < 1
        ) {
            return tmp
        } else {
            tmp?.forEach((part, index, theArray) => {
                let existData = false

                for (let i = 0; i < composedRoleOptions.length; i++) {
                    if (
                        composedRoleOptions[i]?.permissions &&
                        composedRoleOptions[i].permissions?.length > 0
                    ) {
                        for (
                            let j = 0;
                            j < composedRoleOptions[i].permissions.length;
                            j++
                        ) {
                            if (part?.children && part.children?.length > 0) {
                                for (let k = 0; k < part.children.length; k++) {
                                    if (
                                        composedRoleOptions[i].permissions[j]
                                            .roleCategory === part?.key
                                    ) {
                                        let item = {
                                            id: composedRoleOptions[i].id,
                                            name: composedRoleOptions[i].name,
                                            parentKey: part.key,
                                            key: part.children[k].key,
                                            checked:
                                                composedRoleOptions[i]
                                                    .permissions[j][
                                                    part.children[k].key
                                                ],
                                        }

                                        if (
                                            part.children[k]?.dataList &&
                                            Array.isArray(
                                                part.children[k]?.dataList,
                                            ) &&
                                            part.children[k].dataList.length > 0
                                        ) {
                                            let index = part.children[
                                                k
                                            ].dataList.findIndex(
                                                obj =>
                                                    obj.id === item.id &&
                                                    obj.parentKey ===
                                                        item.parentKey &&
                                                    obj.key === item.key,
                                            )

                                            if (index >= 0) {
                                                part.children[k].dataList[
                                                    index
                                                ] = item
                                            } else {
                                                part.children[k].dataList = [
                                                    ...part.children[k]
                                                        .dataList,
                                                    item,
                                                ]
                                            }
                                        } else {
                                            part.children[k].dataList = [item]
                                        }

                                        existData = true
                                    }
                                }
                            }
                        }
                    }
                }

                if (!existData) {
                    for (let k = 0; k < part.children.length; k++) {
                        for (let i = 0; i < composedRoleOptions.length; i++) {
                            let item = {
                                id: composedRoleOptions[i].id,
                                name: composedRoleOptions[i].name,
                                parentKey: part.key,
                                key: part.children[k].key,
                                checked: false,
                            }

                            if (
                                part.children[k]?.dataList &&
                                Array.isArray(part.children[k]?.dataList)
                            ) {
                                let index = part.children[k].dataList.findIndex(
                                    obj =>
                                        obj.id === item.id &&
                                        obj.parentKey === item.parentKey &&
                                        obj.key === item.key,
                                )

                                if (index >= 0) {
                                    part.children[k].dataList[index] = item
                                    continue
                                } else {
                                    part.children[k].dataList = [
                                        ...part.children[k].dataList,
                                        item,
                                    ]
                                }
                            } else {
                                part.children[k].dataList = [item]
                            }
                        }
                    }
                }
            })
        }

        return tmp
    }, [composedRoleOptions])

    const handleUpdateRoles = async () => {
        let hasEmptyPermissions = false
        for (let i = 0; i < composedRoleOptions.length; i++) {
            if (
                !composedRoleOptions[i].permissions ||
                composedRoleOptions[i].permissions.length < 1
            ) {
                hasEmptyPermissions = true
                break
            }
        }

        if (!!hasEmptyPermissions) {
            showNotification(
                severityNotification.warn,
                'Role must have at least one permission !',
            )

            return
        }

        let toPostData = [...composedRoleOptions]

        if (toPostData?.length > 0) {
            let isError = false
            for (let i = 0; i < toPostData?.length; i++) {
                if (
                    toPostData[i]?.id &&
                    toPostData[i]?.permissions &&
                    toPostData[i]?.permissions?.length > 0
                ) {
                    const labelDocument = document.getElementById(
                        'roleLabel-' + toPostData[i].id,
                    )
                    if (labelDocument) {
                        toPostData[i].name = labelDocument?.innerText
                    } else {
                        isError = true
                        break
                    }
                } else {
                    isError = true
                    break
                }
            }

            if (isError) {
                showNotification(
                    severityNotification.warn,
                    'Role must have at least one permission !',
                )

                return
            }

            updateRoles(toPostData)
        }
    }

    const {mutate: updateRoles} = useMutation(
        async toPostData =>
            await createOrUpdateGenericInfo(
                ROLE_PATH + '/bulk',
                user?.id,
                toPostData,
            ),
        {
            onSuccess: data => {
                if (data && data === HTTP_STATUS_OK) {
                    showNotification(
                        severityNotification.success,
                        'Update role permission successfully',
                    )
                }
            },
        },
    )

    const {mutate: mutateRoles} = useMutation(updateRoles => updateRoles, {
        onSuccess: data => {
            if (!!data) {
                if (Array.isArray(data)) {
                    queryClient.setQueryData([QueryKey, user?.id], () => data)
                } else {
                    queryClient.setQueryData([QueryKey, user?.id], oldData => {
                        let newData = [...oldData]
                        let index = newData.findIndex(obj => obj.id === data.id)
                        if (index >= 0) {
                            newData[index] = data
                        } else {
                            newData = [data, ...newData]
                        }

                        return newData
                    })
                }

                setChecked([])
            }
        },
    })

    const handleRefetchRoles = useCallback(
        updateRoles => {
            mutateRoles(updateRoles)
        },
        [mutateRoles],
    )

    useEffect(() => {
        tableEvent.on('RELOAD-DATA-TABLE-' + QueryKey, handleRefetchRoles)

        return () => tableEvent.removeListener('RELOAD-DATA-TABLE-' + QueryKey)
    }, [handleRefetchRoles])

    return (
        <React.Fragment>
            <div className="col-12 px-0 ">
                <div className="flex col-12 align-items-center justify-content-between px-0">
                    <div className="">
                        <h3 className="text-gradient font-bold mb-0">
                            {translateCommons('Role Management')}
                        </h3>
                        <h6 className="my-1">
                            {translateCommons(
                                'This section is used for creating organization roles with a group permissions',
                            )}
                        </h6>
                    </div>
                    <div className="flex">
                        {!disabled && (
                            <div
                                className="flex"
                                style={{
                                    borderRight: '1px solid lightgrey',
                                }}>
                                <div>
                                    <Tooltip
                                        style={{zIndex: 1, textAlign: 'center'}}
                                        target=".tooltip-save"
                                        position="bottom"
                                        content={translateCommons(
                                            'Save changes',
                                        )}
                                    />
                                    <a
                                        onClick={() => handleUpdateRoles()}
                                        className="tooltip-save p-ripple flex p-3 lg:px-3 lg:py-2 align-items-center text-600 hover:text-primary font-medium border-round cursor-pointer transition-duration-150 transition-colors w-full">
                                        <i
                                            className={`pi text-base lg:text-2xl mr-2 lg:mr-0 pi-save`}></i>
                                        <span className="block lg:hidden text-sm">
                                            {translateCommons('Save changes')}
                                        </span>
                                        <Ripple />
                                    </a>
                                </div>
                                <div className="mr-2">
                                    <Tooltip
                                        style={{zIndex: 1, textAlign: 'center'}}
                                        target=".tooltip-add-custom-role"
                                        position="bottom"
                                        content={translateCommons('Add Role')}
                                    />
                                    <a
                                        onClick={() =>
                                            formRef.current.showForm({})
                                        }
                                        className="tooltip-add-custom-role p-ripple flex p-3 lg:px-3 lg:py-2 align-items-center text-600 hover:text-primary font-medium border-round cursor-pointer transition-duration-150 transition-colors w-full">
                                        <i
                                            className={`pi text-base lg:text-2xl mr-2 lg:mr-0 pi-plus`}></i>
                                        <span className="block lg:hidden text-sm">
                                            {translateCommons('Add Role')}
                                        </span>
                                        <Ripple />
                                    </a>
                                </div>
                            </div>
                        )}

                        <Tooltip
                            style={{zIndex: 1, textAlign: 'center'}}
                            target=".tooltip-disabled"
                            position="bottom"
                            content={
                                !!disabled
                                    ? translateCommons('Click to enable')
                                    : translateCommons('Click to disable')
                            }
                        />
                        <a
                            onClick={() => setDisabled(prev => !prev)}
                            className="tooltip-disabled p-ripple flex p-3 lg:px-3 lg:py-2 align-items-center text-600 hover:text-primary font-medium border-round cursor-pointer transition-duration-150 transition-colors w-full">
                            <i
                                className={`px-2 pi text-base lg:text-2xl mr-2 lg:mr-0 ${
                                    disabled ? 'pi-lock' : 'pi-lock-open'
                                }`}></i>
                            <span className="block lg:hidden text-sm">
                                {!!disabled
                                    ? translateCommons('Click to enable')
                                    : translateCommons('Click to disable')}
                            </span>
                            <Ripple />
                        </a>
                    </div>
                </div>
            </div>
            <hr className="highlighter w-full" />
            {roleOptions?.length < 1 ? (
                <div
                    className="flex flex-column align-items-center justify-content-center"
                    style={{minHeight: '70vh'}}>
                    <img
                        src={NoRolesImage}
                        alt="No_Roles"
                        style={{height: '20vh', marginBottom: '8px'}}
                    />
                    <div>
                        <h6 className="mb-0">
                            {translateCommons(
                                'Currently there is no role has been set up yet',
                            )}
                        </h6>
                    </div>
                </div>
            ) : (
                <React.Fragment>
                    <div className="col-12 p-0">
                        <div className="grid p-0 pt-3 pb-2">
                            <div className="col-5 p-0">
                                <p className="mb-3"></p>
                                <h6 className="p-2">
                                    {translateCommons('Active Status')}
                                </h6>
                            </div>
                            <div className="col-7 p-0">
                                <div className="flex flex-no-wrap max-h-3rem">
                                    {composedRoleOptions &&
                                        composedRoleOptions.length > 0 &&
                                        composedRoleOptions.map(
                                            item =>
                                                !!item?.id && (
                                                    <div
                                                        key={`role-${item.id}-${item.name}`}
                                                        className="col-2 flex flex-column justify-content-center align-items-center">
                                                        {!!disabled ? (
                                                            <p
                                                                className={
                                                                    'text-center font-bold text-lg mb-3 prevent-select'
                                                                }
                                                                style={{
                                                                    color: 'lightgray',
                                                                }}>
                                                                {convertKeyToLabel(
                                                                    item.name,
                                                                )}
                                                            </p>
                                                        ) : (
                                                            <p
                                                                id={`roleLabel-${item?.id}`}
                                                                contentEditable
                                                                suppressContentEditableWarning={
                                                                    true
                                                                }
                                                                className="text-center font-bold text-lg mb-3">
                                                                {convertKeyToLabel(
                                                                    item.name,
                                                                )}
                                                            </p>
                                                        )}
                                                        <div>
                                                            <Checkbox
                                                                checked={
                                                                    !!item?.active
                                                                }
                                                                disabled={
                                                                    !!disabled
                                                                }
                                                                onChange={() =>
                                                                    handleOnActiveChange(
                                                                        item.id,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                ),
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 p-0">
                        <div className="grid p-0">
                            <div className="col-5 p-0">
                                {dataList?.map((item, pIdx) => (
                                    <div
                                        key={`p-${item.key}-${pIdx}`}
                                        className="col-12 p-0 ">
                                        <h4
                                            className={`${fontStyle} col-12 mb-0 p-0 h-3rem font-bold`}>
                                            {item.label
                                                ? item.label
                                                : convertKeyToLabel(item.key)}
                                        </h4>
                                        <div>
                                            {item.children?.map(
                                                (childItem, cIdx) => (
                                                    <div
                                                        key={`p${item.key}-c${childItem.key}-${cIdx}`}
                                                        className="col-12 p-0">
                                                        <div className="col-12">
                                                            <h6
                                                                className={`m-0 ${fontStyle} `}
                                                                style={{
                                                                    height: '22px',
                                                                }}>
                                                                {childItem.key ===
                                                                permissionOptionKeys.qualified
                                                                    ? 'Qualified for taking care of patients'
                                                                    : convertKeyToLabel(
                                                                          childItem.key,
                                                                      )}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div
                                id="scrollList"
                                onScroll={e => {}}
                                className="p-0 col-7 overflow-x-auto"
                                style={{scrollBehavior: 'smooth'}}>
                                <div className="col-12 p-0">
                                    {dataList?.map((parentItem, pIdx) => (
                                        <div
                                            key={`p-${parentItem.key}-${pIdx}`}
                                            className="col-12 align-items-center flex-no-wrap p-0">
                                            <div className="col-12 p-0 mt-6">
                                                {parentItem.children?.map(
                                                    (childItem, cIdx) => (
                                                        <div
                                                            key={`p${parentItem.key}-c${childItem.key}-${cIdx}`}
                                                            className="">
                                                            <div className="flex">
                                                                {composedRoleOptions &&
                                                                    composedRoleOptions.length >
                                                                        0 &&
                                                                    composedRoleOptions.map(
                                                                        (
                                                                            item,
                                                                            idx,
                                                                        ) => (
                                                                            <div
                                                                                key={`p${item.key}-${idx}-checkbox`}
                                                                                className="col-2 p-0">
                                                                                <div className="col-12 text-center">
                                                                                    <Checkbox
                                                                                        key={`p${item.key}-c${childItem.key}-${item.name}-${idx}`}
                                                                                        checked={
                                                                                            childItem?.dataList?.find(
                                                                                                x =>
                                                                                                    x.id ===
                                                                                                    item.id,
                                                                                            )
                                                                                                ?.checked
                                                                                        }
                                                                                        disabled={
                                                                                            !!disabled
                                                                                        }
                                                                                        onChange={e =>
                                                                                            handleOnChange(
                                                                                                e,
                                                                                                {
                                                                                                    id: item.id,
                                                                                                    name: item.name,
                                                                                                    checked:
                                                                                                        e.checked,
                                                                                                    parentKey:
                                                                                                        parentItem.key,
                                                                                                    key: childItem.key,
                                                                                                },
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        ),
                                                                    )}
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            )}
            <div className="col-12 p-0">
                <RoleManagementForm ref={formRef} />
            </div>
        </React.Fragment>
    )
}

export default RoleManagement
